/** Marketing CRM (C01-001..007): records + custom-field filters, field-level security, server totals, keys, migration, POs, assistant. */
import { useState, type FormEvent } from 'react';
import { NavLink, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { DEAL_STAGES } from '@qm/contracts/crm';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, Checkbox, DataTable, ErrorNote, Field, Loadable, Money, Notice, ReasonForm, Screen, Stat, Tabs, When, type Column } from '../kit/ui';
import type { CrmEntity, CrmRecord, InvoiceView, MigrationRun } from '../api';
import { ENTITY_REGISTRY, hiddenFields, isFinance, isSuperAdmin, migrationState, visibleRecord } from '../lib/logic';
import { useAdminApi } from './layout';

const ENTITIES: readonly CrmEntity[] = ['companies', 'contacts', 'deals', 'activities', 'projects', 'tasks', 'products', 'employees'];
const COLUMNS: Record<CrmEntity, string[]> = {
  companies: ['name', 'industry', 'website', 'ownerId'], contacts: ['name', 'email', 'phone', 'companyId'], deals: ['title', 'stage', 'valueMinor', 'closeDate'],
  activities: ['type', 'subject', 'dueAt'], projects: ['name', 'status'], tasks: ['title', 'status', 'dueAt'], products: ['name', 'sku', 'priceMinor', 'costMinor'], employees: ['name', 'title', 'salaryMinor'],
};

/** CRM sub-navigation (C01-001..007), filtered by role: API keys are Super Admin only; the rest are all staff (financial fields stay Finance-only inside each screen). */
export const CRM_SECTIONS: readonly { to: string; key: string; show: (roles: readonly string[]) => boolean }[] = [
  { to: '/crm', key: 'portal.admin.crm.sub.records', show: () => true },
  { to: '/crm/invoices', key: 'portal.admin.crm.sub.invoices', show: () => true },
  { to: '/crm/purchase-orders', key: 'portal.admin.crm.sub.pos', show: () => true },
  { to: '/crm/assistant', key: 'portal.admin.crm.sub.assistant', show: () => true },
  { to: '/crm/api-keys', key: 'portal.admin.crm.sub.keys', show: isSuperAdmin },
  { to: '/crm/migration', key: 'portal.admin.crm.sub.migration', show: () => true },
];
export function CrmSubNav() {
  const { t, roles } = useSurface();
  return (
    <nav className="qx-nav" aria-label={t('portal.admin.crm.sub.label')} data-testid="crm-subnav">
      {CRM_SECTIONS.filter((s) => s.show(roles)).map((s) => <NavLink key={s.to} to={s.to} end>{t(s.key)}</NavLink>)}
    </nav>
  );
}

export function CrmPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const entity = (ENTITIES as readonly string[]).includes(sp.get('entity') ?? '') ? (sp.get('entity') as CrmEntity) : 'deals';
  const filters: Record<string, string> = {};
  for (const [k, v] of sp.entries()) if (k !== 'entity' && v) filters[k] = v;
  const list = useResource(`crm:${entity}:${JSON.stringify(filters)}`, () => api.crmList(entity, filters));
  const fields = useResource('crm:customFields', api.customFields);
  const [cfKey, setCfKey] = useState('');
  const [cfVal, setCfVal] = useState('');
  const reg = ENTITY_REGISTRY[entity];
  const hidden = new Set(reg ? hiddenFields(reg, roles) : []);
  const cols: Column<CrmRecord>[] = COLUMNS[entity].filter((c) => !hidden.has(c)).map((c) => ({
    key: c, header: t(`portal.admin.crm.field.${c}`),
    render: (r) => { const v = (reg ? visibleRecord(reg, r, roles) : r)[c]; return c.endsWith('Minor') && typeof v === 'number' ? <Money minor={v} currency={String(r.currency ?? 'AED')} /> : String(v ?? '-'); },
  }));
  const setFilter = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  return (
    <Screen title={t('portal.admin.crm.title')} subtitle={t('portal.admin.crm.subtitle')} testId="crm">
      <CrmSubNav />
      <Tabs label={t('portal.admin.crm.title')} active={entity} onSelect={(v) => setSp({ entity: v })} tabs={ENTITIES.map((e) => ({ id: e, label: t(`portal.admin.crm.entity.${e}`) }))} />
      <div className="qx-toolbar">
        {entity === 'deals' ? (
          <Field label={t('portal.admin.crm.field.stage')}>{(id) => <select id={id} value={filters.stage ?? ''} onChange={(e) => setFilter('stage', e.target.value)}><option value="">{t('portal.admin.any')}</option>{DEAL_STAGES.map((s) => <option key={s} value={s}>{t(`portal.admin.crm.stage.${s}`)}</option>)}</select>}</Field>
        ) : null}
        <Loadable resource={fields}>
          {(defs) => {
            const mine = defs.filter((d) => `${d.entity}s` === entity || (d.entity === 'company' && entity === 'companies'));
            return mine.length ? (
              <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); if (cfKey) setFilter(`cf.${cfKey}`, cfVal.trim()); }}>
                <Field label={t('portal.admin.crm.customField')}>{(id) => <select id={id} value={cfKey} onChange={(e) => setCfKey(e.target.value)}><option value="">-</option>{mine.map((d) => <option key={d.id} value={d.fieldKey}>{d.label ?? d.fieldKey}</option>)}</select>}</Field>
                <Field label={t('portal.admin.crm.value')}>{(id) => <input id={id} value={cfVal} onChange={(e) => setCfVal(e.target.value)} />}</Field>
                <Button type="submit" disabled={!cfKey}>{t('portal.admin.filter')}</Button>
              </form>
            ) : null;
          }}
        </Loadable>
      </div>
      <Loadable resource={list}>{(env) => <DataTable rows={env.data} rowKey={(r) => r.id} columns={cols} empty={t('portal.kit.empty')} />}</Loadable>
      {hidden.size ? <Text tone="muted" data-testid="fls-note">{t('portal.admin.crm.flsNote')}</Text> : null}
    </Screen>
  );
}

/** Invoices: financial fields only for Finance (server omits them; the console never renders a slot for them). */
export function InvoicesPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const { id } = useParams();
  const list = useResource('crm:invoices', () => api.invoices());
  const finance = isFinance(roles);
  const hidden = new Set(hiddenFields('invoice', roles));
  const cols: Column<InvoiceView>[] = [
    { key: 'number', header: t('portal.admin.invoice.number'), render: (i) => <a href={`/crm/invoices/${encodeURIComponent(i.id)}`}>{i.number ?? i.id}</a> },
    { key: 'customer', header: t('portal.admin.invoice.customer'), render: (i) => i.customerName ?? '-' },
    { key: 'status', header: t('portal.admin.status'), render: (i) => <Badge>{i.status}</Badge> },
    ...(!hidden.has('totalMinor') ? [{ key: 'total', header: t('portal.admin.invoice.total'), render: (i: InvoiceView) => (typeof i.totalMinor === 'number' ? <Money minor={i.totalMinor} currency={i.currency} /> : '-') }] : []),
  ];
  return (
    <Screen title={t('portal.admin.invoice.title')} testId="crm-invoices">
      <CrmSubNav />
      {!finance ? <Notice testId="finance-hidden">{t('portal.admin.invoice.financeOnly')}</Notice> : null}
      <Loadable resource={list}>{(rows) => <DataTable rows={rows} rowKey={(i) => i.id} columns={cols} empty={t('portal.kit.empty')} />}</Loadable>
      {id ? <InvoiceDetail id={id} /> : finance ? <NewInvoice onCreated={list.reload} /> : null}
    </Screen>
  );
}

export function InvoiceDetail({ id }: { id: string }) {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const inv = useResource(`crm:invoice:${id}`, () => api.invoice(id));
  const [amount, setAmount] = useState('');
  const adjust = useAction((reason: string) => api.adjustInvoice(id, Math.round(Number(amount) * 100), reason));
  return (
    <Loadable resource={inv}>
      {(raw) => {
        const i = visibleRecord('invoice', raw as unknown as Record<string, unknown>, roles) as Partial<InvoiceView>;
        return (
          <Panel data-testid="invoice-detail">
            <Heading level={2}>{raw.number ?? raw.id}</Heading>
            <div className="qx-grid">
              <Stat label={t('portal.admin.status')} value={raw.status} />
              {typeof i.subtotalMinor === 'number' ? <Stat testId="inv-subtotal" label={t('portal.admin.invoice.subtotal')} value={<Money minor={i.subtotalMinor} currency={raw.currency} />} /> : null}
              {typeof i.taxMinor === 'number' ? <Stat testId="inv-tax" label={t('portal.admin.invoice.tax')} value={<Money minor={i.taxMinor} currency={raw.currency} />} /> : null}
              {typeof i.totalMinor === 'number' ? <Stat testId="inv-total" label={t('portal.admin.invoice.total')} value={<Money minor={i.totalMinor} currency={raw.currency} />} /> : null}
            </div>
            {i.items ? <DataTable rows={i.items} rowKey={(x) => x.id ?? x.description} empty="" columns={[
              { key: 'd', header: t('portal.admin.invoice.item'), render: (x) => x.description },
              { key: 'q', header: t('portal.admin.invoice.qty'), render: (x) => x.quantity },
              { key: 'u', header: t('portal.admin.invoice.unit'), render: (x) => (typeof x.unitPriceMinor === 'number' ? <Money minor={x.unitPriceMinor} currency={raw.currency} /> : '-') },
            ]} /> : null}
            <Text tone="muted">{t('portal.admin.invoice.serverTotals')}</Text>
            {isFinance(roles) ? (
              <div data-testid="invoice-adjust">
                <Field label={t('portal.admin.invoice.adjustment')}>{(fid) => <input id={fid} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />}</Field>
                <ReasonForm label={t('portal.kit.reason')} submitLabel={t('portal.admin.invoice.adjust')} pending={adjust.pending} onSubmit={async (r) => { if (amount && (await adjust.run(r))) inv.reload(); }} />
                <ErrorNote error={adjust.error} />
              </div>
            ) : null}
          </Panel>
        );
      }}
    </Loadable>
  );
}

/** Line items only: subtotal, tax and total are computed server-side from authorised items (C01-003). */
export function NewInvoice({ onCreated }: { onCreated: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [currency, setCurrency] = useState('AED');
  const [vat, setVat] = useState('5');
  const [items, setItems] = useState([{ description: '', quantity: 1, unit: '' }]);
  const create = useAction(api.createInvoice);
  const valid = items.every((i) => i.description.trim() && i.quantity > 0 && Number(i.unit) >= 0 && i.unit !== '');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await create.run({ currency, taxRateBps: Math.round(Number(vat) * 100), items: items.map((i) => ({ description: i.description.trim(), quantity: i.quantity, unitPriceMinor: Math.round(Number(i.unit) * 100) })) });
    if (r) onCreated();
  };
  return (
    <Panel data-testid="invoice-new">
      <Heading level={2}>{t('portal.admin.invoice.new')}</Heading>
      <form onSubmit={submit}>
        <div className="qx-toolbar">
          <Field label={t('portal.admin.invoice.currency')}>{(id) => <select id={id} value={currency} onChange={(e) => setCurrency(e.target.value)}><option>AED</option><option>USD</option></select>}</Field>
          <Field label={t('portal.admin.invoice.vat')}>{(id) => <input id={id} type="number" min="0" max="100" step="0.01" value={vat} onChange={(e) => setVat(e.target.value)} />}</Field>
        </div>
        {items.map((it, n) => (
          <div className="qx-toolbar" key={n}>
            <Field label={t('portal.admin.invoice.item')}>{(id) => <input id={id} value={it.description} onChange={(e) => setItems((xs) => xs.map((x, j) => (j === n ? { ...x, description: e.target.value } : x)))} />}</Field>
            <Field label={t('portal.admin.invoice.qty')}>{(id) => <input id={id} type="number" min="1" value={it.quantity} onChange={(e) => setItems((xs) => xs.map((x, j) => (j === n ? { ...x, quantity: Number(e.target.value) } : x)))} />}</Field>
            <Field label={t('portal.admin.invoice.unit')}>{(id) => <input id={id} type="number" min="0" step="0.01" value={it.unit} onChange={(e) => setItems((xs) => xs.map((x, j) => (j === n ? { ...x, unit: e.target.value } : x)))} />}</Field>
          </div>
        ))}
        <Button onClick={() => setItems((xs) => [...xs, { description: '', quantity: 1, unit: '' }])}>{t('portal.admin.invoice.addItem')}</Button>
        <Text tone="muted">{t('portal.admin.invoice.serverTotals')}</Text>
        <Button type="submit" variant="primary" disabled={!valid || create.pending}>{t('portal.admin.invoice.save')}</Button>
      </form>
      <ErrorNote error={create.error} />
    </Panel>
  );
}

export function PurchaseOrdersPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [productId, setProductId] = useState('');
  const [qty, setQty] = useState(1);
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('AED');
  const create = useAction(api.createPurchaseOrder);
  return (
    <Screen title={t('portal.admin.po.title')} subtitle={t('portal.admin.po.subtitle')} testId="purchase-orders">
      <CrmSubNav />
      <form onSubmit={(e) => { e.preventDefault(); void create.run({ currency, items: [{ productId: productId.trim(), quantity: qty, unitCostMinor: Math.round(Number(cost) * 100) }] }); }}>
        <Field label={t('portal.admin.po.product')}>{(id) => <input id={id} value={productId} onChange={(e) => setProductId(e.target.value)} />}</Field>
        <Field label={t('portal.admin.invoice.qty')}>{(id) => <input id={id} type="number" min="1" value={qty} onChange={(e) => setQty(Number(e.target.value))} />}</Field>
        <Field label={t('portal.admin.po.unitCost')}>{(id) => <input id={id} type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} />}</Field>
        <Field label={t('portal.admin.invoice.currency')}>{(id) => <select id={id} value={currency} onChange={(e) => setCurrency(e.target.value)}><option>AED</option><option>USD</option></select>}</Field>
        <Button type="submit" variant="primary" disabled={!productId.trim() || qty < 1 || cost === '' || create.pending}>{t('portal.admin.po.raise')}</Button>
      </form>
      {create.result ? <Notice tone="good" testId="po-created">{t('portal.admin.po.created', { id: String((create.result as { id?: unknown }).id ?? '') })}</Notice> : null}
      <ErrorNote error={create.error} />
    </Screen>
  );
}

export function AssistantPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [q, setQ] = useState('');
  const ask = useAction(api.assistant);
  return (
    <Screen title={t('portal.admin.assistant.title')} subtitle={t('portal.admin.assistant.subtitle')} testId="crm-assistant">
      <CrmSubNav />
      <form className="qx-toolbar" onSubmit={(e) => { e.preventDefault(); if (q.trim()) void ask.run(q.trim()); }}>
        <Field label={t('portal.admin.assistant.question')}>{(id) => <input id={id} value={q} onChange={(e) => setQ(e.target.value)} />}</Field>
        <Button type="submit" variant="primary" disabled={!q.trim() || ask.pending}>{t('portal.admin.assistant.ask')}</Button>
      </form>
      {ask.result ? (
        ask.result.refused ? <Notice tone="warn" testId="assistant-refused">{t('portal.admin.assistant.refused')}</Notice> : (
          <Panel data-testid="assistant-answer">
            <Text>{ask.result.answer}</Text>
            <ul>{ask.result.citations.map((c) => <li key={`${c.entity}:${c.id}`} data-citation={c.id}>{c.label}</li>)}</ul>
            <Text tone="muted">{t('portal.admin.assistant.model', { model: ask.result.model })}</Text>
          </Panel>
        )
      ) : null}
      <ErrorNote error={ask.error} />
    </Screen>
  );
}

export function ApiKeysPage() {
  const { t, roles, now } = useSurface();
  const api = useAdminApi();
  const keys = useResource('crm:keys', api.apiKeys);
  const [name, setName] = useState('');
  const [write, setWrite] = useState(false);
  const [days, setDays] = useState(90);
  const [secret, setSecret] = useState<string | null>(null);
  const create = useAction(api.createApiKey);
  const revoke = useAction(api.revokeApiKey);
  if (!isSuperAdmin(roles)) return <Screen title={t('portal.admin.keys.title')}><Notice>{t('portal.admin.superAdminOnly')}</Notice></Screen>;
  return (
    <Screen title={t('portal.admin.keys.title')} subtitle={t('portal.admin.keys.subtitle')} testId="crm-keys">
      <CrmSubNav />
      {secret ? <Panel data-testid="key-once"><Text>{t('portal.admin.keys.shownOnce')}</Text><code className="qx-secret">{secret}</code><Button onClick={() => setSecret(null)}>{t('portal.admin.keys.copied')}</Button></Panel> : null}
      <form onSubmit={async (e) => { e.preventDefault(); const r = await create.run({ name: name.trim(), scopes: write ? ['crm:read', 'crm:write'] : ['crm:read'], expiresInDays: days }); if (r) { setSecret(r.secret); setName(''); keys.reload(); } }}>
        <Field label={t('portal.admin.keys.name')}>{(id) => <input id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        <Checkbox label={t('portal.admin.keys.write')} checked={write} onChange={setWrite} />
        <Field label={t('portal.admin.keys.expiry')}>{(id) => <select id={id} value={days} onChange={(e) => setDays(Number(e.target.value))}>{[30, 90, 180, 365].map((d) => <option key={d} value={d}>{d}</option>)}</select>}</Field>
        <Button type="submit" variant="primary" disabled={name.trim().length < 2 || create.pending}>{t('portal.admin.keys.issue')}</Button>
      </form>
      <ErrorNote error={create.error ?? revoke.error} />
      <Loadable resource={keys}>
        {(rows) => <DataTable rows={rows} rowKey={(k) => k.id} empty={t('portal.kit.empty')} columns={[
          { key: 'n', header: t('portal.admin.keys.name'), render: (k) => <>{k.name} <code>{k.prefix}...</code></> },
          { key: 's', header: t('portal.admin.keys.scopes'), render: (k) => k.scopes.join(', ') },
          { key: 'st', header: t('portal.admin.status'), render: (k) => { const st = k.revokedAt ? 'revoked' : k.expiresAt && Date.parse(k.expiresAt) <= now().getTime() ? 'expired' : 'active'; return <Badge tone={st === 'active' ? 'good' : 'bad'} testId={`key-${st}`}>{t(`portal.admin.keys.status.${st}`)}</Badge>; } },
          { key: 'a', header: '', render: (k) => (!k.revokedAt ? <Button onClick={async () => { if (await revoke.run(k.id)) keys.reload(); }}>{t('portal.admin.keys.revoke')}</Button> : null) },
        ]} />}
      </Loadable>
      <Text tone="muted">{t('portal.admin.keys.revokeNote')}</Text>
    </Screen>
  );
}

/** Migration runs: dry-run counts, quarantine and ID map; sign-off only for a non-Hub dry run (C01-005). */
export function MigrationPage() {
  const { t, roles } = useSurface();
  const api = useAdminApi();
  const runs = useResource('crm:migrations', api.migrationRuns);
  const sign = useAction(api.signOffMigration);
  return (
    <Screen title={t('portal.admin.migration.title')} subtitle={t('portal.admin.migration.subtitle')} testId="crm-migration">
      <CrmSubNav />
      <Loadable resource={runs}>
        {(rows) => rows.length === 0 ? <Notice>{t('portal.admin.migration.none')}</Notice> : <>{rows.map((r) => <MigrationCard key={r.id} run={r} canSign={isSuperAdmin(roles)} onSign={async (reason) => { if (await sign.run(r.id, reason)) runs.reload(); }} />)}</>}
      </Loadable>
      <ErrorNote error={sign.error} />
    </Screen>
  );
}

function MigrationCard({ run, canSign, onSign }: { run: MigrationRun; canSign: boolean; onSign: (reason: string) => Promise<void> }) {
  const { t } = useSurface();
  const s = migrationState(run);
  return (
    <Panel data-run={run.id}>
      <Heading level={2}>{t(`portal.admin.migration.mode.${run.mode}`)} <Badge>{t(`portal.admin.migration.status.${run.status}`)}</Badge></Heading>
      <Text>{t('portal.admin.migration.source', { account: run.source.accountId.slice(0, 8), db: run.source.databaseId.slice(0, 8) })}</Text>
      <Text>{t('portal.admin.migration.target', { account: run.target.accountId.slice(0, 8), db: run.target.databaseId.slice(0, 8) })}</Text>
      {s.hubSource ? <Notice tone="bad" testId="hub-blocked">{t('portal.admin.migration.hubBlocked')}</Notice> : null}
      <DataTable rows={run.counts} rowKey={(c) => c.table} empty="" columns={[
        { key: 't', header: t('portal.admin.migration.table'), render: (c) => c.table },
        { key: 's', header: t('portal.admin.migration.sourceRows'), render: (c) => c.source },
        { key: 'g', header: t('portal.admin.migration.targetRows'), render: (c) => c.target },
        { key: 'q', header: t('portal.admin.migration.quarantined'), render: (c) => c.quarantined },
        { key: 'o', header: t('portal.admin.migration.orphans'), render: (c) => c.orphans },
      ]} />
      {s.quarantined ? <Text data-testid="quarantined">{t('portal.admin.migration.quarantineNote', { n: s.quarantined })}</Text> : null}
      {s.canSignOff && canSign ? <ReasonForm testId="migration-signoff" label={t('portal.kit.reason')} submitLabel={t('portal.admin.migration.signOff')} onSubmit={onSign} /> : null}
      <When iso={run.createdAt} />
    </Panel>
  );
}
